// Carga por franja del dia (PP-64 sobre PP-63, rediseño): por planta,
// cuantos viajes salen en cada franja, cuantos siguen sin vehiculo y cuantos
// vehiculos no tienen viaje asignado en la franja. Vive en Viajes, vista
// "Carga por franja" (docs/PLAN-REDISENO-PORTAL.md decision 2); el Inicio
// muestra solo la proxima franja y avisa cuando faltan moviles.
//
// "Vehiculos sin viaje asignado en la franja" y nunca "disponibles": es una
// aproximacion (ADR-025 §4), no hay dato de disponibilidad del conductor.
import type { ProviderSiteLoad } from "@ucorp/contracts";
import { Card, PanelHeader } from "../components/ui/card.js";
import { cn } from "../components/ui/cn.js";
import { Failure } from "../components/ui/failure.js";
import { SkeletonTable } from "../components/ui/skeleton.js";
import { busySlots, overloaded, slotPast } from "../logic/home.js";
import { formatClock, formatDate } from "../logic/requests.js";
import type { ReadState } from "../multi-site/usePolled.js";
import { StaleTag } from "./StaleTag.js";

interface Props {
  id: string;
  state: ReadState<unknown>;
  loads: readonly ProviderSiteLoad[];
  now: number;
  onRetry?: () => void;
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? `1 ${one}` : `${String(n)} ${many}`);

function SiteLoad({ load, now }: { load: ProviderSiteLoad; now: number }): React.JSX.Element {
  const slots = busySlots(load);
  const short = slots.filter((slot) => overloaded(slot) && !slotPast(slot, now)).length;
  return (
    <div role="group" aria-label={`Carga de ${load.siteName}`} className="border-t border-divisor first:border-t-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-3.5 py-2.5">
        <h3 className="m-0 text-sm font-semibold text-texto">{load.siteName}</h3>
        <p className="tabular m-0 text-xs text-texto-suave">
          {formatDate(load.date)} · {plural(load.eligibleVehicleCount, "vehículo elegible", "vehículos elegibles")} en la planta
        </p>
      </div>
      {short > 0 && (
        <p className="m-0 px-3.5 pb-2 text-sm font-semibold text-ambar">
          {short === 1 ? "1 franja con más viajes sin asignar" : `${String(short)} franjas con más viajes sin asignar`} que vehículos sin viaje asignado.
        </p>
      )}
      {slots.length === 0 ? (
        <p className="m-0 px-3.5 pb-3 text-sm text-texto-suave">Sin viajes con horario en el día.</p>
      ) : (
        <div tabIndex={0} role="region" aria-label={`Franjas de ${load.siteName}`} className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-acero-claro">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-micro font-medium uppercase tracking-[0.1em] text-texto-debil">
                <th scope="col" className="px-3.5 py-1.5 font-medium">Franja</th>
                <th scope="col" className="px-3.5 py-1.5 text-right font-medium">Viajes</th>
                <th scope="col" className="px-3.5 py-1.5 text-right font-medium">Sin asignar</th>
                <th scope="col" className="px-3.5 py-1.5 text-right font-medium">Vehículos sin viaje asignado en la franja</th>
                <th scope="col" className="sr-only">Estado de la franja</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((slot) => {
                const short = overloaded(slot);
                const past = slotPast(slot, now);
                const end = formatClock(slot.endAt, load.timezone);
                return (
                  <tr
                    key={slot.startAt}
                    data-overloaded={short ? "true" : undefined}
                    className={cn("h-fila border-t border-divisor", short && !past && "bg-ambar/10 font-semibold text-ambar", past && "text-texto-debil")}
                  >
                    <th scope="row" className="tabular whitespace-nowrap px-3.5 text-left font-medium">
                      {slot.start}
                      {end === "" ? "" : ` a ${end}`}
                    </th>
                    <td className="tabular px-3.5 text-right">{slot.tripCount}</td>
                    <td className="tabular px-3.5 text-right">{slot.unassignedTripCount}</td>
                    <td className="tabular px-3.5 text-right">{slot.freeVehicleCount}</td>
                    <td className="whitespace-nowrap px-3.5 text-xs">{past ? "Pasada" : short ? "Faltan vehículos" : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {load.unscheduledTripCount > 0 && (
        <p className="m-0 px-3.5 py-2 text-xs text-texto-suave">
          {plural(load.unscheduledTripCount, "viaje sin paradas planificadas no entra", "viajes sin paradas planificadas no entran")} en ninguna franja.
        </p>
      )}
    </div>
  );
}

export function SlotLoad({ id, state, loads, now, onRetry }: Props): React.JSX.Element {
  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      <PanelHeader id={id} title="Carga por franja del día">{state.estado === "listo" && state.stale && <StaleTag />}</PanelHeader>
      {state.estado === "cargando" && <SkeletonTable rows={4} cols={5} label="Consultando la carga del día" />}
      {state.estado === "denegado" && (
        <div className="p-3.5">
          <Failure title="La cuenta no tiene acceso a la carga de las plantas." />
        </div>
      )}
      {state.estado === "error" && (
        <div className="p-3.5">
          <Failure title="No se pudo consultar la carga del día." {...(onRetry === undefined ? {} : { onRetry })}>
            Se reintenta sola en un minuto.
          </Failure>
        </div>
      )}
      {state.estado === "listo" &&
        (loads.length === 0 ? (
          <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
            La empresa no está habilitada en ninguna planta.
          </p>
        ) : (
          loads.map((load) => <SiteLoad key={load.siteId} load={load} now={now} />)
        ))}
      <p className="m-0 border-t border-divisor px-3.5 py-2 text-xs leading-normal text-texto-suave">
        Viajes que salen en cada franja de 30 minutos contra vehículos de la nómina sin viaje asignado en la franja. Es una aproximación: no sabe si hay conductor.
      </p>
    </Card>
  );
}
